import { sessionFetch } from './session-fetch';
import type {
  HostingPleskDomainPage,
  HostingPleskBackupState,
  HostingPleskInventoryView,
  HostingPleskSiteProfileInput,
  HostingPleskTechnicalResult,
  HostingPleskTechnicalState,
  HostingSiteDomainType,
  HostingSiteEnvironmentFilter,
} from '@tosho/contracts';
export interface HostingPleskDomainQuery {
  readonly search: string;
  readonly serverId: string;
  readonly client: string;
  readonly owner?: string;
  readonly presence: 'CURRENT' | 'MISSING' | 'ALL';
  readonly domainType?: HostingSiteDomainType;
  readonly environment?: HostingSiteEnvironmentFilter;
  readonly page: number;
  readonly pageSize: number;
}
export class HostingPleskInventoryApiError extends Error {
  public constructor(public readonly code: string) {
    super('Inventaire Plesk indisponible.');
  }
}
async function request<T>(path: string, method = 'GET', csrf?: string, body?: unknown): Promise<T> {
  const response = await sessionFetch('/api/' + path, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    signal: AbortSignal.timeout(12000),
    headers: {
      ...(csrf === undefined ? {} : { 'X-CSRF-Token': csrf }),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const value: unknown = await response.json().catch(() => null);
    throw new HostingPleskInventoryApiError(
      value && typeof value === 'object' && 'code' in value && typeof value.code === 'string'
        ? value.code
        : 'PLESK_INVENTORY_UNAVAILABLE',
    );
  }
  return response.json() as Promise<T>;
}
export const hostingPleskInventoryApi = {
  read: (id: string): Promise<HostingPleskInventoryView> =>
    request(`hosting-servers/${encodeURIComponent(id)}/plesk-inventory`),
  refresh: (
    id: string,
    accessRevision: number,
    inventoryRevision: number | null,
    csrf: string,
  ): Promise<HostingPleskInventoryView> =>
    request(`hosting-servers/${encodeURIComponent(id)}/plesk-inventory`, 'POST', csrf, {
      expectedAccessRevision: accessRevision,
      expectedInventoryRevision: inventoryRevision,
    }),
  list: (query: HostingPleskDomainQuery): Promise<HostingPleskDomainPage> => {
    const params = new URLSearchParams({
      search: query.search,
      client: query.client,
      owner: query.owner ?? 'ALL',
      presence: query.presence,
      domainType: query.domainType ?? 'ALL',
      environment: query.environment ?? 'ALL',
      page: String(query.page),
      pageSize: String(query.pageSize),
    });
    if (query.serverId) params.set('serverId', query.serverId);
    return request('hosting-plesk-domains?' + params.toString());
  },
  assignClient: (
    id: string,
    expectedRevision: number,
    clientId: string | null,
    csrf: string,
    organizationSite = false,
  ): Promise<{ saved: true }> =>
    request(`hosting-plesk-domains/${encodeURIComponent(id)}/client`, 'PATCH', csrf, {
      expectedRevision,
      clientId,
      organizationSite,
    }),
  describeSite: (
    id: string,
    expectedRevision: number,
    profile: HostingPleskSiteProfileInput,
    csrf: string,
  ): Promise<{ saved: true }> =>
    request(`hosting-plesk-domains/${encodeURIComponent(id)}/profile`, 'PATCH', csrf, {
      expectedRevision,
      ...profile,
    }),
  readBackups: (id: string): Promise<HostingPleskBackupState> =>
    request(`hosting-plesk-domains/${encodeURIComponent(id)}/backup-observation`),
  observeBackups: (
    id: string,
    expectedRevision: number,
    expectedAccessRevision: number,
    csrf: string,
  ): Promise<HostingPleskBackupState> =>
    request(`hosting-plesk-domains/${encodeURIComponent(id)}/backup-observation`, 'POST', csrf, {
      expectedRevision,
      expectedAccessRevision,
    }),
  readTechnical: (id: string): Promise<HostingPleskTechnicalState> =>
    request(`hosting-plesk-domains/${encodeURIComponent(id)}/technical-observation`),
  observeTechnical: (
    id: string,
    expectedRevision: number,
    expectedAccessRevision: number,
    csrf: string,
  ): Promise<HostingPleskTechnicalResult> =>
    request(`hosting-plesk-domains/${encodeURIComponent(id)}/technical-observation`, 'POST', csrf, {
      expectedRevision,
      expectedAccessRevision,
    }),
};
export function hostingPleskInventoryError(error: unknown): string {
  if (error instanceof HostingPleskInventoryApiError) {
    if (error.code === 'RECENT_AUTHENTICATION_REQUIRED')
      return 'Reconnectez-vous à Tosho pour lancer cette lecture sensible.';
    if (error.code === 'PLESK_INVENTORY_CONFLICT')
      return 'Ces informations ont changé. Rechargez l’état avant de recommencer.';
    if (error.code === 'PLESK_RATE_LIMITED')
      return 'Trop de tentatives. Patientez une minute avant de réessayer.';
    if (error.code === 'PLESK_NOT_CONFIGURED')
      return 'Le stockage protégé n’est pas configuré sur cet environnement.';
    if (error.code === 'PLESK_INVENTORY_REQUEST_INVALID')
      return 'Vérifiez les filtres et les informations renseignées.';
  }
  return 'La lecture ou l’enregistrement ne peut pas être confirmé. Rechargez les informations avant de réessayer.';
}
